import { useState } from "react";
import { MapPin, EnvelopeSimple, Clock, WhatsappLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import HowToOrder from "@/components/HowToOrder";
import { SITE } from "@/data/site";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const { settings } = useSettings();
  const c = settings.contact || {};
  const cAddress = c.address || SITE.address;
  const cPhone = c.phone || SITE.phone;
  const cWhatsHref = `https://wa.me/${c.whatsapp || SITE.whatsapp}`;
  const cEmail = c.email || SITE.email;
  const hours = [
    { day: "Monday – Friday", time: c.hours_weekday || "9:00 – 18:00" },
    { day: "Saturday", time: c.hours_saturday || "10:00 – 17:00" },
    { day: "Sunday", time: c.hours_sunday || "Closed" },
  ];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "LocalBusiness", "@id": "https://ebikesandgear.co.uk/#store",
    name: SITE.name, url: "https://ebikesandgear.co.uk", email: cEmail, priceRange: "££",
    address: { "@type": "PostalAddress", streetAddress: "St George Lane", addressLocality: "Canterbury", addressRegion: "Kent", postalCode: "CT1 2SY", addressCountry: "GB" },
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "10:00", closes: "17:00" },
    ],
  };

  return (
    <>
      <SEO title="Contact Our E-Bike Shop Kent" description="Visit Ebikes&Gear at St George Lane, Kent CT1 2SY. WhatsApp or email sales@ebikesandgear.co.uk — book your free e-bike test ride today." path="/contact" jsonLd={jsonLd} />
      <PageHero title="Let's talk bikes" subtitle="Questions about a bike, a service or an order? Our team of experts is here to help." image="https://images.unsplash.com/photo-1675798225739-d8919b7a23f7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" crumbs={[{ label: "Contact" }]} />

      <section className="container-px py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          {/* Info */}
          <div className="space-y-4">
            {[
              { I: MapPin, t: "Visit our showroom", v: cAddress },
              { I: WhatsappLogo, t: "WhatsApp", v: `Chat instantly on ${cPhone}`, href: cWhatsHref },
              { I: EnvelopeSimple, t: "Email", v: cEmail, href: `mailto:${cEmail}` },
            ].map(({ I, t, v, href }) => (
              <div key={t} className="flex items-start gap-4 rounded-3xl border border-border bg-card p-5">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><I size={24} weight="bold" /></span>
                <div>
                  <p className="font-bold">{t}</p>
                  {href ? <a href={href} className="text-muted-foreground hover:text-primary">{v}</a> : <p className="text-muted-foreground">{v}</p>}
                </div>
              </div>
            ))}
            <div className="rounded-3xl border border-border bg-card p-5">
              <p className="flex items-center gap-2 font-bold"><Clock size={20} className="text-primary" /> Opening hours</p>
              <ul className="mt-3 space-y-1.5 text-sm">
                {hours.map((h) => <li key={h.day} className="flex justify-between"><span className="text-muted-foreground">{h.day}</span><span className="font-semibold">{h.time}</span></li>)}
              </ul>
            </div>
          </div>

          {/* Form */}
          <div>
            <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="contact-form">
              <h2 className="text-2xl font-black tracking-tight">Send us a message</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <input required placeholder="Name" value={form.name} onChange={set("name")} className="rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary" data-testid="contact-name" />
                <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary" data-testid="contact-email" />
                <input placeholder="Phone" value={form.phone} onChange={set("phone")} className="rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary" data-testid="contact-phone" />
                <input placeholder="Subject" value={form.subject} onChange={set("subject")} className="rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary" data-testid="contact-subject" />
                <textarea required rows={5} placeholder="How can we help?" value={form.message} onChange={set("message")} className="sm:col-span-2 rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary" data-testid="contact-message" />
              </div>
              <button disabled={busy} className="btn-primary mt-5 w-full" data-testid="contact-submit">{busy ? "Sending…" : <>Send message <PaperPlaneTilt size={16} /></>}</button>
            </form>
            <div className="mt-6 overflow-hidden rounded-3xl border border-border">
              <iframe title="map" src={SITE.mapsEmbed} className="h-[300px] w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </div>
      </section>

      <HowToOrder />
    </>
  );
}
